/**
 * DiscussionList Component
 *
 * Discussion browser with tabs, filtering, and sorting.
 *
 * Story FOS-4.1.5: Discussion UI & Guided Flow
 * Task 8: Create DiscussionList view
 *
 * Story FOS-4.2.6: Master Components & Pages
 * Task 2: Migrate to governance-suite
 *
 * Features:
 * - Tabs: "My Discussions" | "Participating" | "All Public"
 * - Card view with stage badge, participant count, last activity
 * - Filter by category, stage, date
 * - Sort by activity, creation date
 *
 * @module components/governance/DiscussionList
 * @see AC-4.1.5.1 - Users can create new discussions with guided question flow
 */

import { useState, useMemo, useCallback } from 'react';
import { Plus, MessageSquare, Users, Filter, Loader2 } from 'lucide-react';
import {
  CATEGORY_METADATA,
  DISCUSSION_CATEGORIES,
  type DiscussionStage,
  type DiscussionCategory,
} from '../../stores/discussionStore';

/**
 * Discussion summary for list display
 */
export interface DiscussionSummary {
  id: string;
  title: string;
  category: DiscussionCategory;
  stage: DiscussionStage;
  proposerId: string;
  proposerName: string;
  participantCount: number;
  commentCount: number;
  createdAt: number;
  lastActivityAt: number;
}

/**
 * Props for DiscussionList component
 */
export interface DiscussionListProps {
  /** List of discussions to display */
  discussions: DiscussionSummary[];
  /** Current user's principal ID */
  currentUserId?: string;
  /** Discussion IDs the user is participating in */
  participatingDiscussionIds?: string[];
  /** Whether data is loading */
  isLoading?: boolean;
  /** Callback when a discussion is selected */
  onSelect: (discussionId: string) => void;
  /** Callback when create button is clicked */
  onCreate?: () => void;
  /** Optional class name */
  className?: string;
}

/**
 * Tab options
 */
type TabOption = 'all' | 'my-discussions' | 'participating';

/**
 * Sort options
 */
type SortOption = 'activity' | 'created';

/**
 * Stage badge colors
 */
const STAGE_COLORS: Record<DiscussionStage, string> = {
  Brainstorm: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  Refining: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  Ready: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
};

/**
 * Category badge colors — derived from the canonical CATEGORY_METADATA
 * (BL-220) so every category, including Personnel / Immutable /
 * Tier1Constitutional, gets a badge without a duplicated hardcoded map.
 */
const CATEGORY_COLORS: Record<DiscussionCategory, string> = Object.fromEntries(
  DISCUSSION_CATEGORIES.map((c) => [c, CATEGORY_METADATA[c].badgeClass]),
) as Record<DiscussionCategory, string>;

/**
 * Format relative time
 */
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

/**
 * Discussion card component
 */
function DiscussionCard({
  discussion,
  onClick,
}: {
  discussion: DiscussionSummary;
  onClick: () => void;
}) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onClick();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <a
      href={`/discussions/${discussion.id}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="block w-full text-left p-4 rounded-lg border border-border bg-card hover:bg-muted/50 transition-colors no-underline text-inherit discussion-card"
      data-testid={`discussion-card-${discussion.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-medium text-foreground line-clamp-2">
          {discussion.title}
        </h3>
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STAGE_COLORS[discussion.stage]}`}
        >
          {discussion.stage}
        </span>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium ${CATEGORY_COLORS[discussion.category]}`}
        >
          {discussion.category}
        </span>
        <span className="text-xs text-muted-foreground">
          by {discussion.proposerName}
        </span>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {discussion.participantCount}
          </span>
          <span className="flex items-center gap-1">
            <MessageSquare className="h-3.5 w-3.5" />
            {discussion.commentCount}
          </span>
        </div>
        <span>{formatRelativeTime(discussion.lastActivityAt)}</span>
      </div>
    </a>
  );
}

/**
 * DiscussionList - Discussion browser with filtering
 *
 * @example
 * ```tsx
 * <DiscussionList
 *   discussions={discussions}
 *   currentUserId={userId}
 *   onSelect={(id) => navigateToDiscussion(id)}
 *   onCreate={() => openCreateModal()}
 * />
 * ```
 */
export function DiscussionList({
  discussions,
  currentUserId,
  participatingDiscussionIds = [],
  isLoading = false,
  onSelect,
  onCreate,
  className = '',
}: DiscussionListProps) {
  const [activeTab, setActiveTab] = useState<TabOption>('all');
  const [categoryFilter, setCategoryFilter] = useState<DiscussionCategory | ''>('');
  const [stageFilter, setStageFilter] = useState<DiscussionStage | ''>('');
  const [sortBy, setSortBy] = useState<SortOption>('activity');

  // Filter and sort discussions
  const filteredDiscussions = useMemo(() => {
    let result = [...discussions];

    // Tab filter
    if (activeTab === 'my-discussions' && currentUserId) {
      result = result.filter(d => d.proposerId === currentUserId);
    } else if (activeTab === 'participating') {
      result = result.filter(d => participatingDiscussionIds.includes(d.id));
    }

    // Category filter
    if (categoryFilter) {
      result = result.filter(d => d.category === categoryFilter);
    }

    // Stage filter
    if (stageFilter) {
      result = result.filter(d => d.stage === stageFilter);
    }

    // Sort
    if (sortBy === 'activity') {
      result.sort((a, b) => b.lastActivityAt - a.lastActivityAt);
    } else {
      result.sort((a, b) => b.createdAt - a.createdAt);
    }

    return result;
  }, [discussions, activeTab, currentUserId, participatingDiscussionIds, categoryFilter, stageFilter, sortBy]);

  const handleTabChange = useCallback((tab: TabOption) => {
    setActiveTab(tab);
  }, []);

  const handleCategoryChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategoryFilter(e.target.value as DiscussionCategory | '');
  }, []);

  const handleStageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStageFilter(e.target.value as DiscussionStage | '');
  }, []);

  const handleSortChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortBy(e.target.value as SortOption);
  }, []);

  return (
    <div className={`space-y-4 ${className}`} data-testid="discussion-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Discussions</h2>
        {onCreate && (
          <button
            onClick={onCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            data-testid="create-discussion-button"
          >
            <Plus className="h-4 w-4" />
            New Discussion
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        <button
          onClick={() => handleTabChange('all')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'all'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-all"
        >
          All Public
        </button>
        <button
          onClick={() => handleTabChange('my-discussions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'my-discussions'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-my-discussions"
        >
          My Discussions
        </button>
        <button
          onClick={() => handleTabChange('participating')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'participating'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="tab-participating"
        >
          Participating
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <select
            value={categoryFilter}
            onChange={handleCategoryChange}
            className="px-3 py-1.5 rounded-md border border-border bg-background text-sm"
            data-testid="category-filter"
          >
            <option value="">All Categories</option>
            {DISCUSSION_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {CATEGORY_METADATA[value].label}
              </option>
            ))}
          </select>
        </div>

        <select
          value={stageFilter}
          onChange={handleStageChange}
          className="px-3 py-1.5 rounded-md border border-border bg-background text-sm"
          data-testid="stage-filter"
        >
          <option value="">All Stages</option>
          <option value="Brainstorm">Brainstorm</option>
          <option value="Refining">Refining</option>
          <option value="Ready">Ready</option>
        </select>

        <div className="ml-auto">
          <select
            value={sortBy}
            onChange={handleSortChange}
            className="px-3 py-1.5 rounded-md border border-border bg-background text-sm"
            data-testid="sort-select"
          >
            <option value="activity">Most Recent Activity</option>
            <option value="created">Newest First</option>
          </select>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div
          className="flex items-center justify-center py-12"
          data-testid="loading-state"
        >
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filteredDiscussions.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-12 text-center"
          data-testid="empty-state"
        >
          <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">
            No discussions found
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {activeTab === 'my-discussions'
              ? "You haven't created any discussions yet."
              : activeTab === 'participating'
                ? "You're not participating in any discussions."
                : 'No discussions match your filters.'}
          </p>
          {onCreate && (
            <button
              onClick={onCreate}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              Start a Discussion
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredDiscussions.map((discussion) => (
            <DiscussionCard
              key={discussion.id}
              discussion={discussion}
              onClick={() => onSelect(discussion.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default DiscussionList;
